/**
 * LocationProfiles — profile list with create/edit/delete forms.
 *
 * Shows when user is authenticated. Allows managing up to 10 location profiles.
 * Updates search point when a profile is selected.
 * Reverts to default location when active profile is deleted.
 */

import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlaceIcon from '@mui/icons-material/Place';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { LocationProfile } from '../types';

const RADIUS_OPTIONS = [2, 5, 10, 15, 20] as const;
const MAX_PROFILES = 10;

interface LocationProfilesProps {
  profiles: LocationProfile[];
  isLoading: boolean;
  activeProfileId: string | null;
  onSelect: (profile: LocationProfile) => void;
  onCreate: (input: { name: string; address: string; radiusKm: 2 | 5 | 10 | 15 | 20 }) => Promise<unknown>;
  onUpdate: (id: string, input: { name?: string; address?: string; radiusKm?: 2 | 5 | 10 | 15 | 20 }) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

interface ProfileFormData {
  name: string;
  address: string;
  radiusKm: 2 | 5 | 10 | 15 | 20;
}

const EMPTY_FORM: ProfileFormData = { name: '', address: '', radiusKm: 10 };

export default function LocationProfiles({
  profiles,
  isLoading,
  activeProfileId,
  onSelect,
  onCreate,
  onUpdate,
  onDelete,
  isCreating,
  isUpdating,
  isDeleting,
}: LocationProfilesProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileFormData>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const isWorking = isCreating || isUpdating || isDeleting;

  const handleOpenCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (profile: LocationProfile) => {
    setEditId(profile.profileId);
    setForm({
      name: profile.name,
      address: profile.address,
      radiusKm: profile.radiusKm,
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!form.name.trim() || form.name.trim().length < 1 || form.name.trim().length > 50) {
      return 'Nimi on pakollinen (1–50 merkkiä).';
    }
    if (!form.address.trim()) {
      return 'Osoite on pakollinen.';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      if (editId) {
        await onUpdate(editId, {
          name: form.name.trim(),
          address: form.address.trim(),
          radiusKm: form.radiusKm,
        });
      } else {
        await onCreate({
          name: form.name.trim(),
          address: form.address.trim(),
          radiusKm: form.radiusKm,
        });
      }
      handleClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Toiminto epäonnistui.';
      setError(msg);
    }
  };

  const handleDelete = async (profileId: string) => {
    try {
      await onDelete(profileId);
    } catch {
      // Error shown via query state
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" component="h3">
            <PlaceIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
            Sijaintiprofiilit
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            disabled={profiles.length >= MAX_PROFILES || isWorking}
            aria-label="Lisää profiili"
          >
            Lisää
          </Button>
        </Box>

        {profiles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Ei tallennettuja profiileja. Luo profiili tallentaaksesi sijainteja.
          </Typography>
        ) : (
          <List dense disablePadding>
            {profiles.map((profile) => (
              <ListItem
                key={profile.profileId}
                disablePadding
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleOpenEdit(profile)}
                      disabled={isWorking}
                      aria-label={`Muokkaa ${profile.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDelete(profile.profileId)}
                      disabled={isWorking}
                      aria-label={`Poista ${profile.name}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemButton
                  selected={activeProfileId === profile.profileId}
                  onClick={() => onSelect(profile)}
                  disabled={isWorking}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {profile.name}
                        {activeProfileId === profile.profileId && (
                          <Chip label="Aktiivinen" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={`${profile.address} • ${profile.radiusKm} km`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {profiles.length >= MAX_PROFILES && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Enimmäismäärä ({MAX_PROFILES}) profiileja saavutettu.
          </Typography>
        )}
      </CardContent>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editId ? 'Muokkaa profiilia' : 'Uusi sijaintiprofiili'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Nimi"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              inputProps={{ maxLength: 50 }}
              helperText={`${form.name.length}/50`}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Osoite"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="esim. Hämeenkatu 1, 33100 Tampere"
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="profile-radius-label">Hakusäde</InputLabel>
              <Select
                labelId="profile-radius-label"
                value={form.radiusKm}
                label="Hakusäde"
                onChange={(e) =>
                  setForm((f) => ({ ...f, radiusKm: e.target.value as ProfileFormData['radiusKm'] }))
                }
              >
                {RADIUS_OPTIONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r} km
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isWorking}>
            Peruuta
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isWorking}
          >
            {isWorking ? <CircularProgress size={20} /> : editId ? 'Tallenna' : 'Luo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
